import React, { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { BRANDS } from "@/i18n/translations";
import { Reveal, KineticLines, Stagger } from "@/components/motion/Reveal";
import ProductCard from "@/components/ProductCard";
import EditorialMarquee from "@/components/EditorialMarquee";
import SmartImg from "@/components/SmartImg";

const CATEGORY_LABELS = {
  gula: { id: "Gula", en: "Sugar" },
  pengembang: { id: "Pengembang Kue", en: "Cake Improvers" },
  meses: { id: "Meses", en: "Sprinkles" },
  pasta: { id: "Pasta", en: "Paste" },
  compound: { id: "Compound", en: "Compound" },
  glaze: { id: "Glaze & Topping", en: "Glaze & Topping" },
};

const BRAND_IMG = {
  claris: "/media/claris-bg.png",
  roman: "https://images.unsplash.com/photo-1599599811214-3d44be99547f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  rimbo: "https://images.unsplash.com/photo-1599599811214-3d44be99547f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  boncoco: "/media/boncoco-bg.png",
};

export default function BrandPage() {
  const { slug } = useParams();
  const { t, loc } = useLang();
  const [activeCat, setActiveCat] = useState("all");

  const brand = BRANDS.find((b) => b.slug === slug);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", slug],
    queryFn: async () => (await api.get(`/products?brand=${slug}`)).data,
    enabled: !!brand,
  });

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map((p) => p.category))).filter(Boolean);
    return cats;
  }, [products]);

  const filtered = activeCat === "all" ? products : products.filter((p) => p.category === activeCat);

  if (slug === "clara") return <Navigate to="/brands/clara" replace />;
  if (!brand) return <Navigate to="/" replace />;

  const dark = ["roman", "rimbo", "boncoco"].includes(slug);

  return (
    <div data-testid={`brand-page-${slug}`} className={dark ? "bg-clara-bg text-clara-text" : ""}>
      {/* HERO */}
      <section className={`relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden ${dark ? "bg-clara-bg text-clara-text" : "bg-ink text-cream"}`}>
        <SmartImg src={BRAND_IMG[slug]} alt={brand.name} priority className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          {brand.logo && (
            <SmartImg src={brand.logo} alt={brand.name} priority className="h-20 md:h-24 max-w-[280px] object-contain mb-8 drop-shadow-lg" />
          )}
          <div className="flex items-center gap-3 mb-6">
            <span className="h-1 w-14" style={{ background: brand.accent }} />
            <span className={`text-[11px] uppercase tracking-[0.3em] ${dark ? "text-clara-accent" : "text-cream/70"}`}>{loc(brand.tagline)}</span>
          </div>
          <h1 className="font-serif text-7xl md:text-9xl leading-[0.85]">
            <KineticLines lines={[brand.name]} />
          </h1>
          <p className={`mt-8 max-w-xl text-lg leading-relaxed ${dark ? "text-clara-text/75" : "text-cream/75"}`}>{loc(brand.desc)}</p>
        </div>
      </section>

      {/* FILTERS + GRID */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-3 mb-12" data-testid="brand-filters">
            {["all", ...categories].map((c) => {
              const active = activeCat === c;
              const activeCls = dark ? "bg-clara-accent text-clara-bg border-clara-accent" : "bg-ink text-cream border-ink";
              const idleCls = dark
                ? "border-clara-text/20 text-clara-text/70 hover:border-clara-accent hover:text-clara-text"
                : "border-line text-inkmute hover:border-ink hover:text-ink";
              return (
                <button
                  key={c}
                  data-testid={`filter-${c}`}
                  onClick={() => setActiveCat(c)}
                  className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-full border transition-colors ${active ? activeCls : idleCls}`}
                >
                  {c === "all" ? t("common.allProducts") : loc(CATEGORY_LABELS[c] || { id: c, en: c })}
                </button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div className={`py-24 text-center ${dark ? "text-clara-text/60" : "text-inkmute"}`}>Loading…</div>
        ) : (
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <ProductCard key={p.slug} product={p} accent={brand.accent} dark={dark} />
            ))}
          </Stagger>
        )}
      </section>

      <EditorialMarquee text={brand.name} dark={dark} />

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className={`p-12 md:p-20 flex flex-col md:flex-row md:items-center justify-between gap-8 ${dark ? "bg-clara-surface text-clara-text border border-clara-text/10" : "bg-ink text-cream"}`}>
          <div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">{t("common.distributorInquiry")}</h2>
            <p className={`mt-4 max-w-lg leading-relaxed ${dark ? "text-clara-text/60" : "text-cream/60"}`}>{loc({ id: "Tertarik menjadi distributor atau memesan sampel? Hubungi tim kami.", en: "Interested in becoming a distributor or requesting a sample? Reach our team." })}</p>
          </div>
          <Link to="/contact" data-testid="brand-cta-btn" className={`group inline-flex items-center gap-2 rounded-full pl-8 pr-6 py-4 text-sm font-semibold uppercase tracking-widest transition-colors duration-300 shrink-0 ${dark ? "bg-clara text-white hover:bg-[#8A362B]" : "bg-cream text-ink hover:bg-claris hover:text-cream"}`}>
            {t("common.requestSample")} <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
